import type { MarketingTranslationKeys } from "../../types.js";

export const marketing: MarketingTranslationKeys = {
  meta: {
    defaultTitle: "HomeExpense — Clarté financière pour votre foyer",
    defaultDescription:
      "Gérez les dépenses du ménage grâce au prorata intelligent, à l'analyse IA, aux budgets et à un coffre-fort de garantie.",
    featuresTitle: "Fonctionnalités — HomeExpense",
    featuresDescription:
      "Calcul au prorata, OCR basé sur l'IA, journal des dépenses, budgets et coffre-fort de garantie pour votre maison.",
    methodologyTitle: "Méthodologie — HomeExpense",
    methodologyDescription:
      "Notre vision du produit : précision, stabilité à long terme et interface utilisateur axée sur les chiffres.",
    legalTitle: "Mentions légales et contact — HomeExpense",
    legalDescription:
      "Confidentialité, conditions, sécurité et comment nous joindre. ",
    privacyTitle: "Politique de confidentialité — HomeExpense",
    privacyDescription:
      "Responsable du traitement, données collectées, finalités, conservation, sous-traitants et vos droits.",
    termsTitle: "Conditions d'utilisation — HomeExpense",
    termsDescription:
      "Conditions générales du site et des applications HomeExpense : compte, usage acceptable, propriété intellectuelle et limitations.",
    pricingTitle: "Tarifs – HomeExpense",
    pricingDescription:
      "Forfaits Maison, Smart scan et Insights : fonctionnalités, limites de l'IA et tarifs de référence pour les ménages.",
  },
  nav: {
    ariaMain: "Navigation principale",
    brand: "AccueilDépenses",
    home: "Maison",
    features: "Caractéristiques",
    methodology: "Méthodologie",
    pricing: "Tarifs",
    legal: "Mentions légales et contacts",
    register: "Créer un compte",
    login: "Se connecter",
    openMenu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
  },
  footer: {
    tagline: "Une gestion claire des dépenses du ménage.",
    platformTitle: "Produit",
    legalTitle: "Légal",
    supportTitle: "Soutien",
    privacy: "Confidentialité",
    terms: "Termes",
    security: "Sécurité",
    contact: "Contact",
    copyright: "© {{year}} HomeExpense. ",
  },
  images: {
    landingHeroAlt: "Tableau de bord financier minimal sur une tablette, tons neutres avec accents bleus.",
    landingVizAlt: "Visualisation abstraite de données financières dans les tons bleu marine, émeraude et doré.",
    featuresHeroAlt: "Bureau avec papeterie financière, lumière naturelle, lignes épurées.",
    featuresProrateAlt: "Tablette affichant des graphiques et des mesures de répartition des dépenses.",
    featuresAiAlt: "Téléphone numérisant un reçu avec des lignes de surbrillance numériques.",
    legalMapAlt: "Carte stylisée en niveaux de gris d’une grille de ville.",
    methodologyHeroAlt: "Détails architecturaux minimaux, lignes épurées et ombres douces.",
    methodologyOfficeAlt: "Intérieur de bureau moderne avec lumière naturelle et mobilier minimaliste.",
  },
  home: {
    heroEyebrow: "Financement des ménages",
    heroTitleLine1: "Clarté financière",
    heroTitleLine2: "pour votre maison",
    heroDescription:
      "Un calcul au prorata intelligent, un journal des dépenses, des budgets, une analyse IA et un coffre-fort de garantie, conçus pour les ménages et la vie partagée.",
    ctaPrimary: "Créez un compte gratuit",
    ctaSecondary: "Découvrez notre méthodologie",
    cardSurplusLabel: "Exemple de coussin mensuel",
    cardSurplusValue: "1 240,00 €",
    highlightsTitle: "Pourquoi HomeExpense",
    highlight1Label: "Se concentrer",
    highlight1Value: "Ménages partagés",
    highlight2Label: "Tarifs",
    highlight2Value: "Niveau gratuit",
    highlight3Label: "Produit",
    highlight3Value: "Améliorations continues",
    highlight4Label: "Confidentialité",
    highlight4Value: "Vos données, votre compte",
    featuresEyebrow: "Domaines d'application principaux",
    featuresTitle: "Des fonctionnalités qui correspondent aux flux de travail réels",
    featuresSubtitle:
      "Les mêmes piliers que vous verrez dans l'application : dépenses, budget, numérisation et coffre-fort, avec une répartition équitable entre les membres.",
    featureProrateTitle: "Calcul au prorata intelligent",
    featureProrateDesc:
      "Répartissez les coûts récurrents et ponctuels entre les membres, les pourcentages et les règles du foyer.",
    featureOcrTitle: "Analyse IA",
    featureOcrDesc:
      "Téléchargez les reçus et laissez le système extraire le montant, le commerçant et une catégorie suggérée à confirmer.",
    featureVaultTitle: "Coffre de garantie",
    featureVaultDesc:
      "Suivez les actifs de la maison avec les dates d'achat et de garantie, les rappels et les documents organisés.",
    visualizeTitle: "Visibilité budgétaire",
    visualizeSubtitle:
      "Barres de progression et vues mensuelles pour les objectifs réservés, dépensés et par catégorie.",
    bar1Label: "Taxes trimestrielles (exemple)",
    bar1Value: "65% réservé",
    bar2Label: "Fonds de rénovation (exemple)",
    bar2Value: "42% de l'objectif",
    ctaBottomTitle: "Prêt à commencer ?",
    ctaBottomSubtitle: "Créez votre compte en quelques secondes et ouvrez le tableau de bord pour configurer votre maison.",
    ctaBottomButton: "Inscrivez-vous gratuitement",
  },
  featuresPage: {
    heroEyebrow: "Produit",
    heroTitle: "Des outils pour le moderne",
    heroTitleItalic: "ménage",
    heroDescription:
      "Plus qu'une liste : un journal avec des filtres, des budgets au prorata, une analyse par IA et un coffre-fort pour les garanties et les documents.",
    f01Label: "Caractéristique 01",
    f01Title: "Calcul au prorata intelligent",
    f01Body:
      "Alignez les dépenses avec les dates d'emménagement, les changements d'adhésion et les règles que vous définissez pour la maison.",
    f01aTitle: "Logique sensible aux dates",
    f01aBody: "Utile lorsque quelqu'un rejoint le groupe en milieu de mois ou que les cotisations changent.",
    f01bTitle: "Divisions pondérées",
    f01bBody: "Pourcentages par membre basés sur ce que le ménage a accepté.",
    profileLabel: "Profil divisé (exemple)",
    demoRowALabel: "ZoneA",
    demoRowAPct: "42%",
    demoRowBLabel: "ZoneB",
    demoRowBPct: "28%",
    demoRowCLabel: "Espaces communs",
    demoRowCPct: "30%",
    f02Label: "Caractéristique 02",
    f02Title: "IA et OCR",
    f02Body:
      "Transformez les reçus papier en champs structurés : montant, date, commerçant et catégorie suggérée.",
    f02Quote:
      "Le flux est conçu pour que vous puissiez consulter et enregistrer en quelques clics après la numérisation.",
    f02QuoteAttribution: "— L'équipe HomeExpense",
    overlayComplete: "Analyse terminée",
    overlayCategoryLine: "Catégorie suggérée • quantité détectée",
    f03Eyebrow: "Caractéristique 03",
    f03Title: "Coffre de garantie",
    f03Subtitle:
      "Enregistrez les appareils et les actifs avec des dates, des documents et des rappels avant la fin de la couverture.",
    vaultB1Title: "Rappels d'expiration",
    vaultB1Body: "Gardez une longueur d’avance sur les fenêtres de maintenance et les dates de fin de garantie.",
    vaultChip1: "Notifications",
    vaultChip2: "Résumés par e-mail",
    vaultB2Title: "Documentation prête pour la réclamation",
    vaultB2Body: "Gardez les preuves et les détails des actifs à portée de main lorsque vous en avez besoin.",
    vaultB3Title: "Actifs identifiables",
    vaultB3Body: "Effacez les références par nom, type et dates clés.",
    vaultB4Title: "Histoire liée",
    vaultB4Body: "Joignez des notes de service ou des factures au même enregistrement d'actif.",
    ctaTitle: "Commencez dès aujourd'hui",
    ctaSubtitle: "Créez votre compte et configurez votre maison en quelques minutes.",
    ctaPrimary: "Créer un compte",
    ctaSecondary: "Lire la méthodologie",
  },
  methodologyPage: {
    heroEyebrow: "Notre méthodologie",
    heroTitle: "Dépenses à domicile :",
    heroTitleAccent: "clarté volontaire",
    heroDescription:
      "Gérer une maison consiste à séquencer les décisions au fil du temps. ",
    precisionTitle: "Précision",
    precisionBody:
      "Nous évitons les raccourcis qui faussent les totaux : les catégories, les répartitions et le mois du ménage restent cohérents dans l'interface utilisateur et l'API.",
    accuracyLabel: "Se concentrer",
    accuracyValue: "Données cohérentes",
    stabilityTitle: "Stabilité",
    stabilityBody:
      "Le budget et le journal examinent le mois et le futur proche : charges récurrentes, prorata et solde du ménage.",
    growthTitle: "Grande image",
    growthSubtitle: "Des résumés qui soutiennent la prochaine décision.",
    riskTitle: "Moins de surprises",
    riskSubtitle: "Garanties et rappels d'obligations et de biens.",
    narrativeEyebrow: "Expérience",
    narrativeQuote:
      "Une maison est un système : des personnes, des dépenses et du temps. ",
    narrativeManifesto: "Manifeste du produit",
    pillar1Num: "01",
    pillar1Title: "Interface utilisateur silencieuse",
    pillar1Body: "Hiérarchie claire pour que le nombre important ait le poids visuel.",
    pillar2Num: "02",
    pillar2Title: "Données avec narration",
    pillar2Body: "Des listes, des totaux et des barres qui expliquent ce qui s'est passé et la suite.",
    pillar3Num: "03",
    pillar3Title: "Outillage ménager",
    pillar3Body: "Maisons, membres, budget, coffre-fort et numérisation en un seul flux.",
    breakTitle: "Conçu pour quiconque gère les finances du ménage.",
    breakCta: "Explorer les fonctionnalités",
  },
  legalPage: {
    heroEyebrow: "Engagement",
    heroTitle: "Confidentialité, sécurité et",
    heroTitleAccent: "conversation ouverte",
    heroDescription:
      "Retrouvez les liens vers notre politique de confidentialité et nos conditions d'utilisation, des informations sur la sécurité et comment nous joindre.",
    controllerName: "GProjectdevelop LLC (États-Unis).",
    controllerTaxNote:
      "La société est en cours de constitution ; les identifiants fiscaux définitifs seront publiés ici dès qu'ils seront disponibles.",
    controllerPresence:
      "Équipe répartie entre Miami (É.-U.) et les Asturies (Espagne), sans adresse unique de bureau physique.",
    privacyTitle: "Confidentialité",
    privacyLead:
      "Nous ne vendons pas vos données financières. ",
    privacyBullet1: "HTTPS pour le trafic client.",
    privacyBullet2: "Les espaces domestiques nécessitent votre compte authentifié.",
    privacyBullet3: "Droit de demander la suppression en vertu de la loi applicable.",
    privacyReadMore: "Lire la politique de confidentialité complète",
    termsSectionLabel: "Accord",
    termsTitle: "Conditions d'utilisation",
    termsBody:
      "Ces conditions encadrent l'accès et l'utilisation du site et des applications HomeExpense. Nous vous invitons à lire le document complet.",
    termsUpdatedLabel: "Dernière mise à jour",
    termsUpdatedValue: "10 mai 2026",
    termsCta: "Voir les conditions d'utilisation",
    securityTitle: "Sécurité",
    securityBody:
      "Infrastructure et pratiques alignées sur les attentes SaaS communes, renforcées là où l'API et l'authentification l'exigent.",
    securityChip1: "HTTPS",
    securityChip2: "Authentification sécurisée",
    contactTitle: "Contact",
    contactLead:
      "Pour l'assistance, la confidentialité ou les données personnelles, écrivez-nous ou utilisez le formulaire ; les demandes relatives à la protection des données peuvent utiliser le même canal.",
    contactMapTitle: "Présence",
    contactMapBody:
      "Équipe répartie entre Miami (Floride, É.-U.) et les Asturies (Espagne). Nous n'exploitons pas de guichet unique avec adresse postale fixe.",
    formName: "Nom et prénom",
    formNamePlaceholder: "Votre nom",
    formEmail: "E-mail",
    formEmailPlaceholder: "vous@email.com",
    formMessage: "Message",
    formMessagePlaceholder: "Comment pouvons-nous vous aider ?",
    formSubmit: "Envoyer un message",
    formSubmitting: "Envoi en cours…",
    formSuccess: "Message envoyé. Nous vous répondrons bientôt.",
    formErrorGeneric: "Impossible d'envoyer le message. Réessayez plus tard.",
    formErrorValidation: "Vérifiez le nom, l'e-mail et le message (minimum 10 caractères).",
    formErrorConfig: "L'envoi du formulaire n'est pas configuré dans cet environnement.",
  },
  privacyPage: {
    documentEyebrow: "Transparence",
    documentTitle: "Politique de confidentialité",
    lastUpdatedLabel: "Dernière mise à jour",
    lastUpdatedValue: "10 mai 2026",
    intro:
      "HomeExpense accorde une grande importance à la confidentialité. La présente politique décrit, de manière générale, quelles données personnelles nous pouvons traiter lorsque vous utilisez notre site web ou nos applications mobiles, dans quels buts et quels sont vos droits. Le service peut évoluer ; si vous résidez dans l’Espace économique européen ou dans d’autres régions soumises à des règles spécifiques, certains points peuvent nécessiter des précisions supplémentaires que vous pouvez demander via les coordonnées indiquées en fin de page.",
    sectionControllerTitle: "1. Responsable du traitement",
    sectionControllerBody:
      "Le responsable du traitement des données personnelles obtenues via le produit HomeExpense, au regard des utilisateurs et visiteurs de nos canaux officiels, est GProjectdevelop LLC, domiciliée aux États-Unis (« nous » ou le « responsable »). L'entité peut encore être en cours de constitution ; les informations d'immatriculation et les identifiants fiscaux définitifs seront mis à jour dans cette politique le cas échéant. Pour toute question relative à cette politique ou à l'exercice de vos droits, vous pouvez nous écrire à l'adresse indiquée à la section Contact (y compris pour un contact « protection des données » lorsque pertinent).",
    sectionDataTitle: "2. Données personnelles susceptibles d’être collectées",
    sectionDataIntro:
      "Selon votre utilisation du service, nous pouvons traiter des catégories telles que les suivantes (toutes ne s’appliquent pas dans chaque cas) :",
    sectionDataBullet1:
      "Données de compte et de profil : identifiant, nom ou pseudonyme, adresse e-mail, préférences de langue ou de fuseau horaire et identifiants de connexion (gérés avec des mesures de sécurité appropriées).",
    sectionDataBullet2:
      "Données du foyer et financières saisies par vous ou des membres autorisés : dépenses, budgets, catégories, documents ou métadonnées liées au coffre-fort, et informations que vous choisissez de stocker dans l’application.",
    sectionDataBullet3:
      "Données techniques et relatives à l’application : type d’appareil, système d’exploitation, version de l’app, identifiants techniques ou de session, journaux d’erreurs et, le cas échéant, données d’usage agrégées pour améliorer la stabilité du service.",
    sectionDataBullet4:
      "Communications que vous nous envoyez volontairement (par exemple messages d’assistance) et, si nécessaire, métadonnées utiles pour gérer l’échange.",
    sectionDataBullet5:
      "Images ou fichiers que vous téléchargez pour des fonctions telles que la numérisation de tickets ; ils peuvent inclure des données visibles sur le document. Évitez d’y inclure des données sensibles non nécessaires.",
    sectionPurposeTitle: "3. Finalités",
    sectionPurposeBody:
      "Nous traitons les données pour fournir et maintenir le service HomeExpense (foyers, dépenses, budget, coffre-fort et fonctions associées), authentifier les utilisateurs, personnaliser l’expérience dans le compte, assurer l’assistance, respecter les obligations légales applicables, renforcer la sécurité et les performances, et communiquer avec vous concernant le service lorsque c’est nécessaire. Si nous introduisions ultérieurement des communications commerciales facultatives, nous demanderions, le cas échéant, votre consentement ou proposerions un mécanisme clair d’opposition, conformément au droit applicable.",
    sectionLegalBasisTitle: "4. Bases juridiques (cadre général)",
    sectionLegalBasisBody:
      "Selon le traitement concerné et la loi applicable (par exemple le Règlement (UE) 2016/679, « RGPD »), les bases juridiques peuvent inclure : l’exécution du contrat ou des conditions du service lorsque le traitement est nécessaire pour vous fournir les fonctionnalités demandées ; l’intérêt légitime du responsable pour garantir la sécurité, l’amélioration technique et la prévention de la fraude, dûment mis en balance avec vos droits ; le respect d’obligations légales ; et le consentement lorsqu’il est requis pour des finalités précises (par exemple certains cookies non essentiels ou communications facultatives). Vous pouvez demander des précisions sur un traitement spécifique via les coordonnées ci-dessous.",
    sectionRetentionTitle: "5. Conservation",
    sectionRetentionBody:
      "Nous conservons les données pendant la durée nécessaire aux finalités décrites et aux obligations légales. Les critères incluent la durée de vie de votre compte, la nécessité de résoudre des incidents ou réclamations, et les délais légaux de prescription ou d’audit. Lorsque les données ne sont plus nécessaires, nous les supprimons ou les anonymisons conformément à nos procédures internes et au droit applicable.",
    sectionThirdPartiesTitle: "6. Sous-traitants et services tiers",
    sectionThirdPartiesBody:
      "Nous pouvons faire appel à des prestataires d’infrastructure, d’hébergement, d’e-mail transactionnel, de surveillance des erreurs ou d’autres outils agissant en tant que sous-traitants pour notre compte, avec des contrats et mesures de sécurité adaptés. Nous pouvons également être tenus de communiquer des données aux autorités si la loi l’exige. Nous ne vendons pas vos données personnelles à des tiers. Si nous transférons des données hors de l’Espace économique européen, nous mettrons en œuvre des garanties reconnues (par exemple les clauses contractuelles types) lorsque cela est pertinent.",
    sectionRightsTitle: "7. Vos droits",
    sectionRightsIntro:
      "Dans la mesure où ils vous sont opposables vis-à-vis du responsable, vous pouvez exercer les droits suivants (sous réserve d’exceptions légales) :",
    sectionRightsBullet1: "Accès à vos données personnelles et aux informations sur le traitement.",
    sectionRightsBullet2: "Rectification des données inexactes ou incomplètes.",
    sectionRightsBullet3: "Effacement (« droit à l’oubli ») lorsque cela est applicable.",
    sectionRightsBullet4: "Limitation du traitement, opposition et, le cas échéant, portabilité des données que vous avez fournies.",
    sectionRightsBullet5:
      "Retrait du consentement lorsque le traitement y est fondé, sans affecter la licéité du traitement antérieur. Vous pouvez également introduire une réclamation auprès de l’autorité de protection des données compétente dans votre pays.",
    sectionMinorsTitle: "8. Mineurs",
    sectionMinorsBody:
      "HomeExpense ne s’adresse pas aux personnes de moins de seize (16) ans. Si vous êtes parent ou tuteur et pensez qu’un mineur nous a transmis des données personnelles sans votre consentement, contactez-nous afin que nous puissions prendre les mesures appropriées.",
    sectionChangesTitle: "9. Modifications de la présente politique",
    sectionChangesBody:
      "Nous pouvons mettre à jour cette politique pour refléter des changements juridiques, techniques ou liés au produit. Nous publierons la version en vigueur sur cette page et, lorsque la modification est substantielle, nous nous efforcerons de vous en informer par des moyens raisonnables (par exemple un avis dans l’application ou un e-mail). Nous vous invitons à consulter régulièrement la politique.",
    sectionContactTitle: "10. Contact",
    sectionContactBody:
      "Pour exercer vos droits, poser des questions relatives à la confidentialité ou demander des informations complémentaires sur le traitement de vos données, écrivez-nous à :",
    contactEmail: "contacto@gprojectdevelop.com",
  },
  termsPage: {
    documentEyebrow: "Conditions",
    documentTitle: "Conditions d'utilisation",
    lastUpdatedLabel: "Dernière mise à jour",
    lastUpdatedValue: "10 mai 2026",
    intro:
      "Les présentes conditions régissent l'accès et l'utilisation du site home-expense.gprojectdevelop.com, des applications mobiles HomeExpense et des services associés proposés par GProjectdevelop LLC (« nous », « le prestataire »). En créant un compte, en téléchargeant l'application ou en utilisant le service, vous confirmez avoir lu et accepté ces conditions. Sinon, n'utilisez pas le service. Il s'agit d'une première version indicative ; une relecture juridique est recommandée.",
    sectionPartyTitle: "1. Parties et statut",
    sectionPartyBody:
      "Le service est fourni par GProjectdevelop LLC, domiciliée aux États-Unis. Les informations sociétaires et fiscales définitives pourront être publiées sur ces pages. « Utilisateur » désigne la personne physique ou morale pour laquelle vous agissez.",
    sectionServiceTitle: "2. Description du service",
    sectionServiceBody:
      "HomeExpense propose des outils numériques pour organiser les dépenses du foyer, budgets, membres, coffre-fort documentaire et fonctions auxiliaires (y compris, selon le forfait, des fonctions d'IA). Le service est fourni « en l'état » et peut évoluer. Nous ne sommes pas conseillers financiers, fiscaux ou juridiques ; les calculs et suggestions sont informatifs.",
    sectionAccountTitle: "3. Compte, âge et sécurité",
    sectionAccountBody:
      "Vous devez fournir des informations d'inscription exactes et garder vos identifiants confidentiels. Vous êtes responsable de l'activité sur votre compte sauf accès non autorisé signalé sans retard injustifié. Le service ne s'adresse pas aux moins de 16 ans. Nous pouvons refuser, suspendre ou fermer un compte en cas de manquement grave.",
    sectionAcceptableUseTitle: "4. Usage acceptable",
    sectionAcceptableUseIntro: "Vous vous engagez à ne pas utiliser le service pour :",
    sectionAcceptableUseBullet1:
      "Violer la loi applicable, porter atteinte aux droits de tiers ou accéder à des systèmes sans autorisation.",
    sectionAcceptableUseBullet2:
      "Introduire des logiciels malveillants, surcharger l'infrastructure ou tester des vulnérabilités sans autorisation.",
    sectionAcceptableUseBullet3:
      "Revendre ou exploiter le service en concurrence sans accord écrit.",
    sectionAcceptableUseBullet4:
      "Télécharger un contenu illicite ou des données personnelles de tiers sans base légale.",
    sectionContentTitle: "5. Données et contenus",
    sectionContentBody:
      "Vous conservez la propriété des informations saisies. Vous nous accordez une licence limitée pour les héberger et les traiter afin de fournir le service, conformément à la politique de confidentialité.",
    sectionFeesTitle: "6. Tarifs",
    sectionFeesBody:
      "Certaines fonctions peuvent être gratuites ou payantes selon le forfait publié. Les prix peuvent être modifiés avec un préavis raisonnable. Taxes et frais de tiers (stores) peuvent s'appliquer.",
    sectionIpTitle: "7. Propriété intellectuelle",
    sectionIpBody:
      "Le logiciel, la marque et les contenus du service (hors vos données) appartiennent au prestataire ou à ses concédants.",
    sectionDisclaimerTitle: "8. Absence de garanties",
    sectionDisclaimerBody:
      "Dans toute la mesure permise par la loi, le service est fourni sans garantie expresse ou implicite.",
    sectionLiabilityTitle: "9. Limitation de responsabilité",
    sectionLiabilityBody:
      "Dans la mesure maximale permise, nous déclinons toute responsabilité pour dommages indirects ou perte de profits. Notre responsabilité agrégée sur douze mois ne dépassera pas les sommes payées pour les services payants ou, à défaut, 50 €, sous réserve des minima légaux impératifs.",
    sectionIndemnityTitle: "10. Indemnisation",
    sectionIndemnityBody:
      "Vous nous indemniserez pour les réclamations de tiers liées à votre mauvais usage du service ou au non-respect des présentes conditions.",
    sectionTerminationTitle: "11. Résiliation",
    sectionTerminationBody:
      "Vous pouvez cesser d'utiliser le service et demander la fermeture du compte. Nous pouvons suspendre l'accès en cas de manquement grave ou d'exigence légale.",
    sectionLawTitle: "12. Droit applicable",
    sectionLawBody:
      "Sauf dispositions impératives de votre pays de résidence, les présentes conditions sont régies par le droit des États-Unis et, pour les litiges liés à une prestation depuis la Floride, le droit de l'État de Floride, sans égard aux règles de conflit de lois, sans préjudice des droits du consommateur dans l'UE.",
    sectionChangesTitle: "13. Modifications",
    sectionChangesBody:
      "Nous pouvons modifier ces conditions en publiant une nouvelle version et la date de mise à jour. Les changements importants feront l'objet d'un avis raisonnable lorsque possible.",
    sectionContactTitle: "14. Contact",
    sectionContactBody:
      "Pour toute question sur ces conditions ou le service, écrivez à :",
    contactEmail: "contacto@gprojectdevelop.com",
  },
  pricingPage: {
    heroEyebrow: "Forfaits",
    heroTitle: "Une tarification simple",
    heroSubtitle:
      "Trois niveaux : noyau sans IA, analyse des reçus par IA avec un plafond quotidien et informations intelligentes avec davantage d'analyses. ",
    leadTitle: "Transparence",
    leadBody:
      "Les niveaux payants reflètent le coût estimé du traitement de l’IA plus une marge modeste. ",
    noPaymentNote: "Les paiements via l'application ne sont pas encore activés : vous pouvez vous inscrire et utiliser le produit sans processus de paiement.",
    priceNote: "Prix ​​de référence ; ",
    plan1Name: "Maison",
    plan1Tagline: "Produit de base complet, pas de fonctionnalités d'IA",
    plan1Price: "0 €",
    plan1Period: "/mois",
    plan1RecurringCaption: "Libre pour toujours",
    plan1F1: "Journal des dépenses, calcul au prorata, budget et coffre-fort de garantie",
    plan1F2: "Membres du foyer et règles de partage personnalisables",
    plan1F3: "Pas de numérisation de reçus IA ni de suggestions automatisées",
    plan2Name: "Analyse intelligente",
    plan2Tagline: "Maison et numérisation IA",
    plan2Price: "4,99 €",
    plan2Period: "/mois",
    plan2RecurringCaption: "Prix ​​mensuel de référence",
    plan2Popular: "Meilleur équilibre",
    plan2F1: "Tout à la maison",
    plan2F2: "Jusqu'à 5 analyses de reçus AI par jour",
    plan2F3: "Extraction du montant, du commerçant et de la catégorie suggérée",
    plan3Name: "Connaissances",
    plan3Tagline: "Plus d’IA et de coups de pouce financiers",
    plan3Price: "12,99 €",
    plan3Period: "/mois",
    plan3RecurringCaption: "Prix ​​mensuel de référence",
    plan3F1: "Tout dans Smart Scan",
    plan3F2: "Jusqu'à 15 analyses IA par jour",
    plan3F3: "Suggestions intelligentes sur les modèles de dépenses et les opportunités",
    cta: "Créer un compte",
    includesLabel: "Comprend",
  },
};
